import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Minus } from "@phosphor-icons/react";
import { listRecipes, compareRecipes, NUTRIENT_LABELS, NUTRIENT_UNITS, MACRO_KEYS, MINERAL_KEYS, VITAMIN_KEYS } from "../lib/api";
import NutrientView from "../components/NutrientView";

function fmt(v) {
  if (v == null || Number.isNaN(v)) return "0";
  const n = Number(v);
  if (Math.abs(n) >= 100) return n.toFixed(0);
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

function RecipePicker({ label, value, onChange, recipes, testId }) {
  return (
    <div className="bg-card p-4" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">{label}</div>
      <select value={value || ""} onChange={e => onChange(e.target.value || null)}
        data-testid={`${testId}-select`}
        className="w-full h-10 px-2 border border-border bg-background focus:outline-none focus:border-primary transition-colors duration-200">
        <option value="">— Choose recipe —</option>
        {recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
    </div>
  );
}

export default function RecipeCompare() {
  const [recipes, setRecipes] = useState([]);
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listRecipes().then(d => {
      setRecipes(d.recipes || []);
      if ((d.recipes || []).length >= 2) {
        setA(d.recipes[0].id); setB(d.recipes[1].id);
      }
    }).catch(() => toast.error("Failed to load recipes"));
  }, []);

  useEffect(() => {
    if (a && b && a !== b) {
      setLoading(true);
      compareRecipes(a, b).then(setResult).catch(() => toast.error("Compare failed"))
        .finally(() => setLoading(false));
    } else {
      setResult(null);
    }
  }, [a, b]);

  const ra = result?.a; const rb = result?.b; const winners = result?.winners || {};
  const wa = Object.values(winners).filter(v => v === "a").length;
  const wb = Object.values(winners).filter(v => v === "b").length;

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="compare-page">
      <div className="pb-6 border-b border-border">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Side-by-side</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Compare recipes</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">Pick two recipes to compare macros, minerals and vitamins per serving. The one with more of "good" nutrients (protein, fiber, iron, etc.) wins; the one with less of "limit" nutrients (sodium, sugar, saturated fat, calories) also wins.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
        <RecipePicker label="Recipe A" value={a} onChange={setA} recipes={recipes} testId="picker-a" />
        <RecipePicker label="Recipe B" value={b} onChange={setB} recipes={recipes} testId="picker-b" />
      </div>

      {recipes.length < 2 && (
        <div className="border border-dashed border-border p-8 text-center font-mono text-sm text-muted-foreground">
          Save at least 2 recipes to compare.
        </div>
      )}

      {a && b && a === b && (
        <div className="border border-primary/40 bg-primary/5 p-4 font-mono text-sm text-primary">Pick two different recipes.</div>
      )}

      {loading && <div className="font-mono text-sm text-muted-foreground">Computing…</div>}

      {ra && rb && (
        <>
          {/* headline scoreboard */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {[{ r: ra, wins: wa, side: "a", label: "A" }, { r: rb, wins: wb, side: "b", label: "B" }].map(({ r, wins, side, label }) => (
              <div key={side} className={`p-6 ${wa === wb ? "bg-card" : (wins > (side === "a" ? wb : wa) ? "bg-primary text-primary-foreground" : "bg-card")}`} data-testid={`scorecard-${side}`}>
                <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${wa === wb ? "text-muted-foreground" : (wins > (side === "a" ? wb : wa) ? "opacity-80" : "text-muted-foreground")}`}>Recipe {label}</div>
                <div className="font-display font-black text-3xl tracking-tight mt-1">{r.name}</div>
                <div className="mt-2 font-mono text-sm">
                  {r.ingredients.length} ings · {r.servings} serving{r.servings > 1 ? "s" : ""} · {Math.round(r.per_serving?.calories || 0)} kcal
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <div className="font-mono text-5xl font-medium tracking-tighter">{wins}</div>
                  <div className="font-mono text-xs uppercase tracking-[0.15em]">nutrient wins</div>
                </div>
                {(r.diet_tags || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {r.diet_tags.slice(0, 5).map(t => (
                      <span key={t} className={`font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${wa === wb ? "border-border" : (wins > (side === "a" ? wb : wa) ? "border-white/30" : "border-border")}`}>{t.replace("_", "-")}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Side-by-side full-profile dashboards (all 44 nutrients) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Recipe A · {ra.name}</div>
              <NutrientView
                nutrients={ra.per_serving || {}}
                defaultTab="standard"
                defaultView="dashboard"
                testIdPrefix="compare-a-nutrient-view"
              />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Recipe B · {rb.name}</div>
              <NutrientView
                nutrients={rb.per_serving || {}}
                defaultTab="standard"
                defaultView="dashboard"
                testIdPrefix="compare-b-nutrient-view"
              />
            </div>
          </div>

          {/* nutrient winner rows — full schema (all 44 keys) */}
          <div className="border border-border" data-testid="compare-table">
            <div className="grid grid-cols-[1fr_1.2fr_60px_1.2fr] gap-2 items-center px-4 py-3 border-b border-border bg-muted/50 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <div>Nutrient</div>
              <div className="text-right">Recipe A</div>
              <div className="text-center">Winner</div>
              <div className="text-left">Recipe B</div>
            </div>
            {[...MACRO_KEYS, ...MINERAL_KEYS, ...VITAMIN_KEYS].map(k => {
              const va = Number((ra.per_serving || {})[k] || 0);
              const vb = Number((rb.per_serving || {})[k] || 0);
              const winner = winners[k];
              return (
                <div key={k} className="grid grid-cols-[1fr_1.2fr_60px_1.2fr] gap-2 items-center px-4 py-2.5 border-b border-border last:border-b-0" data-testid={`row-${k}`}>
                  <div className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">{NUTRIENT_LABELS[k]}</div>
                  <div className={`text-right font-mono text-sm ${winner === "a" ? "font-bold text-primary" : ""}`}>
                    {fmt(va)} <span className="text-muted-foreground text-[10px]">{NUTRIENT_UNITS[k]}</span>
                  </div>
                  <div className="flex items-center justify-center">
                    {winner === "tie" ? <Minus size={12} className="text-muted-foreground" /> : (
                      <Trophy size={14} weight="fill" className="text-primary" style={{ transform: winner === "b" ? "scaleX(-1)" : "" }} />
                    )}
                  </div>
                  <div className={`text-left font-mono text-sm ${winner === "b" ? "font-bold text-primary" : ""}`}>
                    {fmt(vb)} <span className="text-muted-foreground text-[10px]">{NUTRIENT_UNITS[k]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
